import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getAllSessions, deleteSession, updateSession, saveSession } from '../utils/uploadSessionsDB';
import {
  uploadMultipart,
  completeMultipart,
  fetchSessionStatus,
  confirmDrive,
} from '../utils/uploadStrategies';
import { matchesPersistedFile } from '../utils/fileIdentity';
import { API_BASE_URL } from '../API/api';
import { getIdToken } from '../utils/authSession';

const UploadsContext = createContext();

export const useUploads = () => {
  const context = useContext(UploadsContext);
  if (!context) {
    throw new Error('useUploads must be used within an UploadsProvider');
  }
  return context;
};

export const UploadsProvider = ({ children }) => {
  const [uploads, setUploads] = useState([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(null); // upload id or null
  const cancelHandlersRef = React.useRef({});
  const expandHandlersRef = React.useRef({});

  const TAB_LOCK_DURATION_MS = 30_000;
  const TAB_LOCK_REFRESH_MS = 10_000;

  const tabId = React.useRef(
    (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : String(Date.now()) + Math.random().toString(36).slice(2),
  );
  const activeRunnersRef = React.useRef({}); // { [localId]: { cancel, lockTimer } }

  const getAuthToken = async () => {
    try {
      const idToken = await getIdToken();
      if (idToken) return String(idToken).trim();
    } catch { /* ignore */ }
    return null;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const records = await getAllSessions();
      if (cancelled || records.length === 0) return;
      const hydrated = records.map((rec) => ({
        id: rec.localId,
        folderName: rec.folderName,
        files: [{ name: rec.fileName, size: rec.fileSize, lastModified: rec.lastModified }],
        fileStatuses: { 0: 'interrupted' },
        fileProgress: {
          0: rec.strategy === 'multipart' && rec.partSize && rec.fileSize
            ? Math.round(((rec.completedParts?.length || 0) * rec.partSize / rec.fileSize) * 100)
            : rec.fileSize
              ? Math.round(((rec.bytesUploaded || 0) / rec.fileSize) * 100)
              : 0,
        },
        status: rec.status === 'uploading' ? 'interrupted' : rec.status,
        createdAt: new Date(rec.createdAt).toISOString(),
        persisted: true,
        strategy: rec.strategy,
        record: rec,
      }));
      setUploads((prev) => [...hydrated, ...prev]);

      try {
        if (hydrated.some((u) => u.status === 'interrupted' || u.status === 'failed')) {
          if (!sessionStorage.getItem('rsaUploads:popupShown')) {
            setIsPopupOpen(true);
            sessionStorage.setItem('rsaUploads:popupShown', '1');
          }
        }
      } catch { /* sessionStorage can throw in some privacy modes */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const addUpload = useCallback((upload) => {
    const newUpload = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      folderName: upload.folderName || 'Unknown',
      files: upload.files || [],
      fileStatuses: upload.fileStatuses || {},
      fileProgress: upload.fileProgress || {},
      status: 'uploading', // 'uploading', 'completed', 'error', 'cancelled'
      createdAt: new Date().toISOString(),
      ...upload,
    };

    setUploads((prev) => [newUpload, ...prev]);
    setIsPopupOpen(true);
    return newUpload.id;
  }, []);

  const updateUpload = useCallback((id, updates) => {
    setUploads((prev) =>
      prev.map((upload) =>
        upload.id === id ? { ...upload, ...updates } : upload
      )
    );
  }, []);

  const removeUpload = useCallback((id) => {
    setUploads((prev) => prev.filter((upload) => upload.id !== id));
  }, []);

  const dismissInterrupted = useCallback(async (id) => {
    setUploads((prev) => prev.filter((u) => u.id !== id));
    await deleteSession(id).catch(() => {});
  }, []);

  const resumeUpload = useCallback(async (localId, file, overrideRecord = null) => {
    const entry = uploads.find((u) => u.id === localId);
    // Use overrideRecord (from restartUpload's fresh-init path) if provided,
    // otherwise fall back to the hydrated record on the entry. Either is
    // required — bail if we have neither.
    const record = overrideRecord || entry?.record;
    if (!record) return;

    if (!matchesPersistedFile(file, record)) {
      setUploads((prev) => prev.map((u) => u.id === localId
        ? { ...u, status: 'mismatch' }
        : u));
      return;
    }

    const now = Date.now();
    if (
      record.tabLockUntil
      && record.tabLockUntil > now
      && record.tabLockOwner
      && record.tabLockOwner !== tabId.current
    ) {
      setUploads((prev) => prev.map((u) => u.id === localId
        ? { ...u, status: 'locked' }
        : u));
      return;
    }

    await updateSession(localId, {
      tabLockUntil: now + TAB_LOCK_DURATION_MS,
      tabLockOwner: tabId.current,
    }).catch(() => {});
    const lockTimer = setInterval(() => {
      updateSession(localId, {
        tabLockUntil: Date.now() + TAB_LOCK_DURATION_MS,
        tabLockOwner: tabId.current,
      }).catch(() => {});
    }, TAB_LOCK_REFRESH_MS);

    setUploads((prev) => prev.map((u) => u.id === localId
      ? { ...u, status: 'uploading', fileStatuses: { 0: 'uploading' } }
      : u));

    const token = await getAuthToken();
    const api = { apiBaseUrl: API_BASE_URL, projectId: record.projectId, authToken: token };

    let cancelled = false;
    activeRunnersRef.current[localId] = {
      cancel: () => { cancelled = true; clearInterval(lockTimer); },
      lockTimer,
    };

    try {
      const status = await fetchSessionStatus({ strategy: record.strategy, sessionId: record.sessionId, api });
      if (status.status === 'expired') {
        await updateSession(localId, { status: 'failed' }).catch(() => {});
        setUploads((prev) => prev.map((u) => u.id === localId
          ? { ...u, status: 'failed', fileStatuses: { 0: 'failed' } }
          : u));
        return;
      }

      if (record.strategy === 'multipart') {
        const completedParts = status.completedParts || [];
        const session = {
          s3UploadId: record.sessionId.s3UploadId,
          s3Key: record.sessionId.s3Key,
          partSize: record.partSize,
          numParts: Math.ceil(record.fileSize / record.partSize),
          completedParts,
          fileIndex: localId, // unique token per upload — avoids _fileIndex collisions
        };
        const accum = [...completedParts];
        const { completedParts: finalParts } = await uploadMultipart({
          file,
          session,
          onProgress: ({ loadedBytes, totalBytes }) => {
            const pct = Math.min(90, Math.round((loadedBytes / totalBytes) * 90));
            setUploads((prev) => prev.map((u) => u.id === localId
              ? { ...u, fileProgress: { 0: pct } }
              : u));
          },
          onPartComplete: async (part) => {
            accum.push(part);
            await updateSession(localId, { completedParts: [...accum] }).catch(() => {});
          },
          awaitPause: null,
          isCancelled: () => cancelled,
          registerXhr: () => {},
          unregisterXhr: () => {},
          api,
        });

        setUploads((prev) => prev.map((u) => u.id === localId
          ? { ...u, fileStatuses: { 0: 'finalizing' }, fileProgress: { 0: 95 } }
          : u));

        await completeMultipart({
          s3Key: record.sessionId.s3Key,
          s3UploadId: record.sessionId.s3UploadId,
          parts: finalParts,
          file,
          googleDriveId: record.folderGoogleDriveId,
          existingXmlAction: record.existingXmlAction || '',
          warnOnExistingRouteXml: record.warnOnExistingRouteXml,
          api,
        });
      } else if (record.strategy === 'drive-direct') {
        const startByte = typeof status.bytesUploaded === 'number' && status.bytesUploaded >= 0
          ? status.bytesUploaded
          : 0;
        const driveFileId = await new Promise((resolve, reject) => {
          const worker = new Worker(
            new URL('../components/Projects/ProjectView/FileExplorer/driveUploadWorker.js', import.meta.url),
          );
          worker.onmessage = (e) => {
            const msg = e.data;
            if (msg.type === 'progress') {
              setUploads((prev) => prev.map((u) => u.id === localId
                ? { ...u, fileProgress: { 0: msg.percent } }
                : u));
              updateSession(localId, { bytesUploaded: msg.loaded }).catch(() => {});
            } else if (msg.type === 'complete') {
              worker.terminate();
              resolve(msg.driveFileId);
            } else if (msg.type === 'error') {
              worker.terminate();
              reject(new Error(msg.message));
            }
          };
          worker.onerror = (err) => { worker.terminate(); reject(err); };
          worker.postMessage({
            type: 'start',
            resumableUri: record.sessionId.resumableUri,
            file,
            chunkSize: record.partSize,
            fileSize: record.fileSize,
            startByte,
          });
        });

        await confirmDrive({
          driveFileId,
          file,
          googleDriveId: record.folderGoogleDriveId,
          resumableUri: record.sessionId.resumableUri,
          api,
        });
      } else {
        // Small-file strategy doesn't resume — user must restart instead.
        throw new Error('small-file strategy is restart-only');
      }

      await deleteSession(localId).catch(() => {});
      setUploads((prev) => prev.filter((u) => u.id !== localId));
    } catch (err) {
      console.error('[resumeUpload] failed:', err);
      setUploads((prev) => prev.map((u) => u.id === localId
        ? { ...u, status: 'interrupted', fileStatuses: { 0: 'interrupted' } }
        : u));
    } finally {
      clearInterval(lockTimer);
      delete activeRunnersRef.current[localId];
    }
  }, [uploads]);

  const cancelResume = useCallback(async (localId) => {
    const runner = activeRunnersRef.current[localId];
    if (runner) runner.cancel();
    await deleteSession(localId).catch(() => {});
    setUploads((prev) => prev.filter((u) => u.id !== localId));
  }, []);

  const restartUpload = useCallback(async (localId, file) => {
    const entry = uploads.find((u) => u.id === localId);
    if (!entry) return;
    const record = entry.record;

    // No record at all — nothing to restart; just drop the UI entry.
    if (!record) {
      await deleteSession(localId).catch(() => {});
      setUploads((prev) => prev.filter((u) => u.id !== localId));
      return;
    }

    // No file provided — just discard (user clicked the X equivalent).
    if (!file) {
      await deleteSession(localId).catch(() => {});
      setUploads((prev) => prev.filter((u) => u.id !== localId));
      return;
    }

    // Delete the old record so we can't accidentally resume it.
    await deleteSession(localId).catch(() => {});

    const token = await getAuthToken();
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    setUploads((prev) => prev.map((u) => u.id === localId
      ? { ...u, status: 'uploading', fileProgress: { 0: 0 }, fileStatuses: { 0: 'uploading' } }
      : u));

    try {
      if (record.strategy === 'multipart') {
        const init = await fetch(`${API_BASE_URL}/api/projects/${record.projectId}/upload/init-multipart`, {
          method: 'POST',
          credentials: 'include',
          headers,
          body: JSON.stringify({
            filename: file.name,
            fileSize: file.size,
            mimeType: file.type || 'application/octet-stream',
            googleDriveId: record.folderGoogleDriveId,
          }),
        }).then((r) => r.json());

        const freshRecord = {
          ...record,
          localId,
          fileName: file.name,
          fileSize: file.size,
          lastModified: file.lastModified,
          sessionId: {
            s3UploadId: init.s3UploadId,
            s3Key: init.s3Key,
            bucket: init.bucket || null,
            resumableUri: null,
          },
          partSize: init.partSize,
          completedParts: [],
          bytesUploaded: 0,
          status: 'uploading',
          tabLockUntil: null,
          tabLockOwner: null,
        };
        await saveSession(freshRecord).catch(() => {});
        setUploads((prev) => prev.map((u) => u.id === localId
          ? { ...u, record: freshRecord }
          : u));

        await resumeUpload(localId, file, freshRecord);
      } else if (record.strategy === 'drive-direct') {
        const init = await fetch(`${API_BASE_URL}/api/projects/${record.projectId}/upload/init-drive`, {
          method: 'POST',
          credentials: 'include',
          headers,
          body: JSON.stringify({
            filename: file.name,
            fileSize: file.size,
            mimeType: file.type || 'application/octet-stream',
            googleDriveId: record.folderGoogleDriveId,
          }),
        }).then((r) => r.json());

        const freshRecord = {
          ...record,
          localId,
          fileName: file.name,
          fileSize: file.size,
          lastModified: file.lastModified,
          sessionId: {
            resumableUri: init.resumableUri,
            s3UploadId: null,
            s3Key: null,
            bucket: null,
          },
          partSize: init.chunkSize,
          bytesUploaded: 0,
          completedParts: [],
          status: 'uploading',
          tabLockUntil: null,
          tabLockOwner: null,
        };
        await saveSession(freshRecord).catch(() => {});
        setUploads((prev) => prev.map((u) => u.id === localId
          ? { ...u, record: freshRecord }
          : u));

        await resumeUpload(localId, file, freshRecord);
      } else {
        // Small-file: can't resume via multipart/drive-direct paths. Dispatch
        // an event so a FileExplorer listener can preload UploadModal with this
        // file. If no listener is wired, we just drop the entry — the user can
        // manually re-upload through the normal UI.
        window.dispatchEvent(new CustomEvent('rsa-restart-small-upload', {
          detail: {
            projectId: record.projectId,
            folderGoogleDriveId: record.folderGoogleDriveId,
            file,
          },
        }));
        setUploads((prev) => prev.filter((u) => u.id !== localId));
      }
    } catch (err) {
      console.error('[restartUpload] failed:', err);
      setUploads((prev) => prev.map((u) => u.id === localId
        ? { ...u, status: 'failed' }
        : u));
    }
  }, [uploads, resumeUpload]);

  const clearAllUploads = useCallback(() => {
    setUploads([]);
  }, []);

  const registerCancelHandler = useCallback((id, handler) => {
    cancelHandlersRef.current[id] = handler;
  }, []);

  const unregisterCancelHandler = useCallback((id) => {
    delete cancelHandlersRef.current[id];
  }, []);

  const registerExpandHandler = useCallback((id, handler) => {
    expandHandlersRef.current[id] = handler;
  }, []);

  const unregisterExpandHandler = useCallback((id) => {
    delete expandHandlersRef.current[id];
  }, []);

  const expandUpload = useCallback((id) => {
    const handler = expandHandlersRef.current[id];
    if (handler) handler();
  }, []);

  const requestCancel = useCallback((id) => {
    setShowCancelConfirm(id);
  }, []);

  const confirmCancel = useCallback((id) => {
    setShowCancelConfirm(null);
    const handler = cancelHandlersRef.current[id];
    if (handler) {
      handler();
    }
  }, []);

  const dismissCancel = useCallback(() => {
    setShowCancelConfirm(null);
  }, []);

  const togglePopup = useCallback(() => {
    setIsPopupOpen((prev) => !prev);
  }, []);

  const openPopup = useCallback(() => {
    setIsPopupOpen(true);
  }, []);

  const closePopup = useCallback(() => {
    setIsPopupOpen(false);
  }, []);

  return (
    <UploadsContext.Provider
      value={{
        uploads,
        isPopupOpen,
        addUpload,
        updateUpload,
        removeUpload,
        clearAllUploads,
        togglePopup,
        openPopup,
        closePopup,
        registerCancelHandler,
        unregisterCancelHandler,
        requestCancel,
        confirmCancel,
        dismissCancel,
        showCancelConfirm,
        registerExpandHandler,
        unregisterExpandHandler,
        expandUpload,
        dismissInterrupted,
        resumeUpload,
        restartUpload,
        cancelResume,
      }}
    >
      {children}
    </UploadsContext.Provider>
  );
};
